import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

/**
 * LAYOUTSYSTEMET — felles byggeklosser for sider og skjemaer.
 *
 * Reglene (begrunnet i docs/ui-polish-spesifikasjon-2026-07.md, «Layout»):
 *
 * 1. Bredden følger innholdet. En side velger én rolle: `form` (48 rem) for
 *    skjema i én kolonne, `form-wide` (64 rem) for skjema med to
 *    seksjonskolonner, `content` (72 rem) for rapporter, detaljvisninger og
 *    e-post, `page` (96 rem) for lister og tabeller, `full` bare for flater
 *    som trenger hver piksel (tavler, prospektering).
 * 2. Ett ankerpunkt. Hovedfeltet har en sentrert innholdskolonne på maks
 *    96 rem (Layout.tsx). Sidene venstrejusteres inne i den, så tittelen står
 *    på samme sted når man bytter side. Ingen sentrering av enkeltsider.
 * 3. Rytme: 24 px fra sidehode til innhold, 32 px mellom seksjoner, 16 px
 *    mellom felt i en seksjon, 8 px fra etikett til felt. Mer luft mellom
 *    grupper enn i dem.
 */

const pageWidths = {
  form: "max-w-form",
  "form-wide": "max-w-form-wide",
  content: "max-w-content",
  page: "max-w-page",
  full: "",
} as const;

export type PageWidth = keyof typeof pageWidths;

export const Page = ({
  width = "page",
  className,
  children,
}: {
  width?: PageWidth;
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn("flex w-full flex-col gap-6", pageWidths[width], className)}
  >
    {children}
  </div>
);

export type Crumb = { to: string; label: ReactNode };

/**
 * Sidehode: brødsmuler › tittel (+ valgfri beskrivelse) til venstre og
 * sidens handlinger til høyre. Hovedhandlingen står alltid ytterst til høyre.
 */
export const PageHeader = ({
  title,
  description,
  crumbs,
  leading,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  crumbs?: Crumb[];
  /** Avatar eller ikon foran tittelen. */
  leading?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <div className={cn("flex flex-col gap-1", className)}>
    {crumbs && crumbs.length > 0 && (
      <nav
        aria-label="Brødsmuler"
        className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
      >
        {crumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1">
            <Link
              to={crumb.to}
              className="rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              {crumb.label}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </span>
        ))}
      </nav>
    )}
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 items-center gap-3">
        {leading}
        <h1 className="page-title truncate">{title}</h1>
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
    {description && (
      <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
    )}
  </div>
);

/**
 * En innholdsseksjon på en side (ikke i et skjema): seksjonstittel med
 * valgfri beskrivelse og handlinger, så innholdet.
 */
export const PageSection = ({
  title,
  description,
  actions,
  className,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section className={cn("flex flex-col gap-3", className)}>
    {(title || actions) && (
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          {title && <h2 className="section-title">{title}</h2>}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);
